;(function () {
  if (customElements.get('ewg-report-query')) return

  class EWGReportQuery extends HTMLElement {
    constructor() {
      super()
      this.countAnimationFrame = null
      this.boundHandlers = {
        loading: this.handleLoading.bind(this),
        ready: this.handleReady.bind(this),
        empty: this.handleEmpty.bind(this),
        error: this.handleEmpty.bind(this),
        providerClick: this.handleProviderClick.bind(this),
        documentClick: this.handleDocumentClick.bind(this),
        documentKeydown: this.handleDocumentKeydown.bind(this),
        getReportClick: this.handleGetReportClick.bind(this)
      }
    }

    connectedCallback() {
      this.cacheDOMElements()
      this.setupEventListeners()
      this.renderShareLinks()

      window.WDEwgReportStore?.init().then(state => {
        if (state?.currentOption && state?.information) this.render(state)
        else this.handleEmpty()
      })
    }

    disconnectedCallback() {
      document.removeEventListener('wd:ewg-loading', this.boundHandlers.loading)
      document.removeEventListener('wd:ewg-ready', this.boundHandlers.ready)
      document.removeEventListener('wd:ewg-empty', this.boundHandlers.empty)
      document.removeEventListener('wd:ewg-error', this.boundHandlers.error)
      document.removeEventListener('click', this.boundHandlers.documentClick)
      document.removeEventListener('keydown', this.boundHandlers.documentKeydown)
      this.filterOptions?.removeEventListener('click', this.boundHandlers.providerClick)
      this.fullReportLink?.removeEventListener('click', this.boundHandlers.getReportClick)

      if (this.countAnimationFrame) {
        cancelAnimationFrame(this.countAnimationFrame)
        this.countAnimationFrame = null
      }
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect()
        this.visibilityObserver = null
      }
    }

    cacheDOMElements() {
      this.totalCount = this.querySelector('.ewg-report-number')
      this.totalCountSpan = this.querySelector('.ewg-report-number-total-count')
      this.gaugeProgress = this.querySelector('.ewg-report-gauge-progress')
      this.titleProvider = this.querySelector('.ewg-report-title-provider')
      this.location = this.querySelector('.ewg-report-location')
      this.population = this.querySelector('.ewg-report-population')
      this.source = this.querySelector('.ewg-report-source')
      this.dataSource = this.querySelector('.ewg-report-data-source')
      this.filterOptions = this.querySelector('.ewg-report-filters-options')
      this.providerCurrent = this.querySelector('.ewg-report-provider-current')
      this.providerDropdown = this.querySelector('.ewg-report-provider-select')
      this.copyButton = this.querySelector('.ewg-report-button--link')
      this.facebookLink = this.querySelector('.ewg-report-share-facebook')
      this.twitterLink = this.querySelector('.ewg-report-share-twitter')
      this.fullReportLink = this.querySelector('.ewg-report-full-report-link')
    }

    setupEventListeners() {
      document.addEventListener('wd:ewg-loading', this.boundHandlers.loading)
      document.addEventListener('wd:ewg-ready', this.boundHandlers.ready)
      document.addEventListener('wd:ewg-empty', this.boundHandlers.empty)
      document.addEventListener('wd:ewg-error', this.boundHandlers.error)
      document.addEventListener('click', this.boundHandlers.documentClick)
      document.addEventListener('keydown', this.boundHandlers.documentKeydown)
      this.filterOptions?.addEventListener('click', this.boundHandlers.providerClick)
      this.fullReportLink?.addEventListener('click', this.boundHandlers.getReportClick)
    }

    handleGetReportClick() {
      if (typeof window.WDSendEvent !== 'function') return
      window.WDSendEvent('wd_get_report_click')
    }

    handleLoading(event) {
      this.setLoadingState(Boolean(event.detail?.isLoading))
    }

    handleReady(event) {
      this.classList.remove('empty')
      document.documentElement.classList.remove('ewg-report-invalid')
      this.render(event.detail)
    }

    handleEmpty() {
      this.classList.add('empty')
      this.setLoadingState(false)
      if (!new URLSearchParams(window.location.search).has('zip')) return
      document.documentElement.classList.add('ewg-report-invalid')
      this.renderEmpty()
    }

    renderEmpty() {
      const na = 'N/A'
      if (this.countAnimationFrame) {
        cancelAnimationFrame(this.countAnimationFrame)
        this.countAnimationFrame = null
      }
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect()
        this.visibilityObserver = null
      }
      if (this.titleProvider) this.titleProvider.textContent = na
      if (this.providerCurrent) this.providerCurrent.textContent = na
      if (this.location) this.location.textContent = na
      if (this.population) this.population.textContent = na
      if (this.source) this.source.textContent = na
      if (this.dataSource) this.dataSource.textContent = na
      if (this.totalCount) this.totalCount.textContent = na
      if (this.totalCountSpan) this.totalCountSpan.textContent = na
      if (this.gaugeProgress) this.gaugeProgress.style.strokeDasharray = '0 100'
      if (this.filterOptions) this.filterOptions.replaceChildren()
    }

    handleProviderClick(event) {
      const button = event.target.closest('.ewg-report-filter-button')
      if (!button || button.classList.contains('ewg-report-filter-button--active')) {
        this.providerDropdown?.removeAttribute('open')
        return
      }

      this.providerDropdown?.removeAttribute('open')
      window.WDEwgReportStore?.selectProvider(button.dataset.filter)
    }

    handleDocumentClick(event) {
      if (!this.providerDropdown?.contains(event.target)) this.providerDropdown?.removeAttribute('open')
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.providerDropdown?.open) return
      this.providerDropdown.removeAttribute('open')
      this.providerDropdown.querySelector('summary')?.focus()
    }

    setLoadingState(isLoading) {
      this.classList.toggle('loading', isLoading)
      this.ariaBusy = isLoading ? 'true' : 'false'
    }

    render(state) {
      if (!state?.currentOption || !state?.information) return

      this.renderSystemInformation(state.currentOption, state.location, state.information)
      this.renderGauge(state.information)
      this.renderDataSource(state.information)
      this.renderProviderOptions(state.systemList, state.currentOption)
    }

    renderSystemInformation(option, zipLocation, information) {
      const systemName = option.SystemName || ''
      const location = zipLocation || option.CityServed || ''

      if (this.titleProvider) this.titleProvider.textContent = systemName
      if (this.providerCurrent) this.providerCurrent.textContent = systemName
      if (this.location) this.location.textContent = location
      if (this.population) {
        const population = Number(information?.Population)
        this.population.textContent = Number.isFinite(population)
          ? population.toLocaleString('en-US')
          : information?.Population || ''
      }
      if (this.source) this.source.textContent = option.PrimSource_fulltext || ''
    }

    renderDataSource(information) {
      if (!this.dataSource) return
      const source = this.decodeHtml(information?.exceedsList?.[0]?.ContaminantTimeFrame || '').trim()
      if (source) this.dataSource.textContent = source
    }

    decodeHtml(value) {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = String(value || '')
      return textarea.value.replace(/<[^>]+>/g, '')
    }

    renderGauge(information) {
      const certifiedCount = Number(information.totalContaminantsCount) || 0
      const totalCount = Number(information.totalAllCount) || 0
      const progress = totalCount > 0 ? Math.min(Math.max(certifiedCount / totalCount, 0), 1) * 100 : 0

      this.animateCount(certifiedCount)
      if (this.totalCountSpan) this.totalCountSpan.textContent = totalCount
      if (this.gaugeProgress) this.gaugeProgress.style.strokeDasharray = `${progress} 100`
    }

    renderProviderOptions(systemList, currentOption) {
      if (!this.filterOptions || !systemList?.length) return

      const fragment = document.createDocumentFragment()
      systemList.forEach(system => {
        if (!system.PWS || !system.SystemName) return

        const isActive = system.PWS === currentOption.PWS
        const button = document.createElement('button')
        button.type = 'button'
        button.className = `ewg-report-filter-button p2-s${isActive ? ' ewg-report-filter-button--active' : ''}`
        button.setAttribute('role', 'option')
        button.setAttribute('aria-selected', isActive ? 'true' : 'false')
        button.dataset.filter = system.PWS
        button.textContent = system.SystemName
        fragment.appendChild(button)
      })

      this.filterOptions.replaceChildren(fragment)
    }

    renderShareLinks() {
      const pageUrl = window.location.href
      const encodedUrl = encodeURIComponent(pageUrl)

      if (this.copyButton) this.copyButton.dataset.value = pageUrl
      if (this.facebookLink) {
        this.facebookLink.href = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
      }
      if (this.twitterLink) {
        const text = encodeURIComponent(document.title)
        this.twitterLink.href = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${text}`
      }
    }

    animateCount(targetValue, duration = 1500) {
      if (!this.totalCount) return

      if (this.countAnimationFrame) cancelAnimationFrame(this.countAnimationFrame)
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect()
        this.visibilityObserver = null
      }

      const section = this.closest('.ewg-report-result-section')
      if (section?.classList.contains('hidden')) {
        this.totalCount.textContent = '0'
        this.visibilityObserver = new MutationObserver(() => {
          if (section.classList.contains('hidden')) return
          this.visibilityObserver.disconnect()
          this.visibilityObserver = null
          this.animateCount(targetValue, duration)
        })
        this.visibilityObserver.observe(section, { attributes: true, attributeFilter: ['class'] })
        return
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.totalCount.textContent = targetValue
        return
      }

      const startTime = performance.now()
      const animate = currentTime => {
        if (currentTime >= startTime + duration) {
          this.totalCount.textContent = targetValue
          this.countAnimationFrame = null
          return
        }

        const progress = Math.min((currentTime - startTime) / duration, 1)
        const easedProgress = 1 - Math.pow(1 - progress, 4)
        this.totalCount.textContent = Math.floor(targetValue * easedProgress)
        this.countAnimationFrame = requestAnimationFrame(animate)
      }

      this.totalCount.textContent = '0'
      this.countAnimationFrame = requestAnimationFrame(animate)
    }
  }

  class EWGReportAccess extends HTMLElement {
    constructor() {
      super()
      this.storageKey = 'WD-ewg-report-access-state'
      this.handleSubscribeSuccess = this.handleSubscribeSuccess.bind(this)
      this.handleAccessRequest = this.handleAccessRequest.bind(this)
      this.handleModalShow = this.handleModalShow.bind(this)
      this.handleSendEmail = this.handleSendEmail.bind(this)
      this.handleViewReport = this.handleViewReport.bind(this)
      this.handleFormSubmit = this.handleFormSubmit.bind(this)
      this.handleBusyClick = this.handleBusyClick.bind(this)
      this.handleBusyKeydown = this.handleBusyKeydown.bind(this)
      this.handleEmailInput = this.handleEmailInput.bind(this)
    }

    connectedCallback() {
      this.modal = this.closest('modal-container')
      this.emailButton = this.querySelector('.ewg-report-access-modal__email-button')
      this.viewButton = this.querySelector('.ewg-report-access-modal__view-button')
      this.toastEl = this.modal?.querySelector('.ewg-report-access-modal__toast')
      this.emailInput = this.querySelector('input[name="email"]')
      this.emailError = this.querySelector('.ewg-report-access-modal__email-error')
      this.subscribeForm = this.querySelector('subscribe-form form')
      if (this.emailInput && this.emailError) this.emailInput.insertAdjacentElement('afterend', this.emailError)
      this.patchModalHide()
      document.addEventListener('subscribe-form:success', this.handleSubscribeSuccess)
      document.addEventListener('wd:ewg-report-access-request', this.handleAccessRequest)
      document.addEventListener('keydown', this.handleBusyKeydown, true)
      this.modal?.addEventListener('modal:show', this.handleModalShow)
      this.modal?.addEventListener('click', this.handleBusyClick, true)
      this.emailButton?.addEventListener('click', this.handleSendEmail)
      this.viewButton?.addEventListener('click', this.handleViewReport)
      this.subscribeForm?.addEventListener('submit', this.handleFormSubmit, true)
      this.emailInput?.addEventListener('input', this.handleEmailInput)
      this.setState(this.getInitialState())
    }

    disconnectedCallback() {
      document.removeEventListener('subscribe-form:success', this.handleSubscribeSuccess)
      document.removeEventListener('wd:ewg-report-access-request', this.handleAccessRequest)
      document.removeEventListener('keydown', this.handleBusyKeydown, true)
      this.modal?.removeEventListener('modal:show', this.handleModalShow)
      this.modal?.removeEventListener('click', this.handleBusyClick, true)
      this.emailButton?.removeEventListener('click', this.handleSendEmail)
      this.viewButton?.removeEventListener('click', this.handleViewReport)
      this.subscribeForm?.removeEventListener('submit', this.handleFormSubmit, true)
      this.emailInput?.removeEventListener('input', this.handleEmailInput)
      clearTimeout(this.toastTimer)
      this.restoreModalHide()
    }

    // AI REVIEW / ACCEPTED RISK: busy 时锁定弹窗（覆盖 hide、禁用关闭、拦截 Escape）是产品约定，
    // 发送邮件 / 打开 PDF 过程中不允许关闭。短请求结束后会 setBusy(false) 恢复，无需改逻辑。
    patchModalHide() {
      if (!this.modal || this.modal.ewgOriginalHide) return
      this.modal.ewgOriginalHide = this.modal.hide.bind(this.modal)
      this.modal.hide = () => {
        if (this.busy) return
        this.modal.ewgOriginalHide()
      }
    }

    restoreModalHide() {
      if (!this.modal?.ewgOriginalHide) return
      this.modal.hide = this.modal.ewgOriginalHide
      delete this.modal.ewgOriginalHide
    }

    setBusy(isBusy) {
      this.busy = isBusy
      this.modal?.classList.toggle('is-busy', isBusy)
      const closeButton = this.modal?.querySelector('.modal-close')
      if (closeButton) closeButton.disabled = isBusy
    }

    handleBusyClick(event) {
      if (!this.busy) return
      event.preventDefault()
      event.stopImmediatePropagation()
    }

    handleBusyKeydown(event) {
      if (!this.busy || !this.modal?.hasAttribute('open')) return
      if (event.key !== 'Escape' && event.key !== 'Esc') return
      event.preventDefault()
      event.stopImmediatePropagation()
    }

    isSubscribed() {
      // AI REVIEW / ACCEPTED RISK: 订阅态只用于本页弹窗 UI，不是服务端权限。无需后端鉴权。
      return (
        this.dataset.customerSubscribed === 'true' ||
        this.dataset.state === 'subscribed' ||
        this.dataset.state === 'success'
      )
    }

    getInitialState() {
      // AI REVIEW / ACCEPTED RISK: localStorage 只恢复解锁 UI，不是授权凭证。完整报告门槛无需后端。
      if (this.dataset.state === 'success') return 'success'
      if (this.dataset.customerSubscribed === 'true') return 'subscribed'
      if (this.dataset.state === 'subscribed') return 'subscribed'
      if (this.dataset.customerLoggedIn === 'true') return 'unsubscribed'
      const storedState = window.localStorage.getItem(this.storageKey)
      if (storedState === 'subscribed' || storedState === 'success') return 'subscribed'
      return 'unsubscribed'
    }

    handleFormSubmit(event) {
      if (this.busy) {
        event.preventDefault()
        event.stopImmediatePropagation()
        return
      }

      const validationMessage = this.getEmailValidationMessage()
      if (validationMessage) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.showEmailError(validationMessage)
        return
      }

      if (this.isSubscribed()) {
        event.preventDefault()
        event.stopImmediatePropagation()
        this.handleSendEmail()
        return
      }

      this.setBusy(true)
      const subscribeForm = event.target.closest('subscribe-form')
      if (!subscribeForm || subscribeForm.ewgBusyPatched) return

      subscribeForm.ewgBusyPatched = true
      const originalShowSubmitMessage = subscribeForm.showSubmitMessage?.bind(subscribeForm)
      subscribeForm.showSubmitMessage = (message, type) => {
        if (type === 'error') this.setBusy(false)
        if (type === 'success') return
        originalShowSubmitMessage?.(message, type)
      }
    }

    getEmailValidationMessage() {
      const email = this.querySelector('input[name="email"]')?.value.trim() || ''
      if (!email) return 'Please enter your email address.'
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        return window.accessibilityStrings?.subscriptionInvalidEmail || 'Please enter a valid email address.'
      }
      return ''
    }

    handleEmailInput() {
      if (!this.getEmailValidationMessage()) this.clearEmailError()
    }

    showEmailError(message) {
      this.emailInput?.setAttribute('aria-invalid', 'true')
      this.emailInput?.focus()
      if (!this.emailError || !message) return
      this.emailError.textContent = message.startsWith('*') ? message : `*${message}`
      this.emailError.hidden = false
    }

    clearEmailError() {
      this.emailInput?.setAttribute('aria-invalid', 'false')
      if (this.emailError) {
        this.emailError.textContent = ''
        this.emailError.hidden = true
      }
    }

    async handleSubscribeSuccess(event) {
      // AI REVIEW / ACCEPTED RISK: 订阅成功只解锁本页完整报告 UI，不换服务端凭证。无需后端鉴权。
      const fromModal = this.contains(event.target)
      const fromPageSubscription = Boolean(event.target.closest('.ewg-subscription'))
      const email = event.detail?.email || this.querySelector('input[name="email"]')?.value.trim()
      if (email) this.subscribedEmail = email

      if (!fromModal && !fromPageSubscription) {
        this.setState('subscribed')
        this.setBusy(false)
        return
      }

      const subscribeForm = event.target.closest('subscribe-form')
      const submitButton = subscribeForm?.querySelector('button[type="submit"]')
      this.setBusy(true)
      window.queueMicrotask(() => {
        submitButton?.classList.add('loading')
        if (submitButton) submitButton.disabled = true
      })

      try {
        const result = await window.WDEwgReportStore?.emailPdf(email)
        if (!result) throw new Error('Unable to send water report')
        if (Number(result.statusCode) === 429 || String(result.message || '').toLowerCase() === 'too many requests') {
          throw result
        }

        this.setState('success')
        if (fromPageSubscription) {
          this.keepSuccessOnShow = true
          this.modal?.show()
        }
      } catch (error) {
        this.setState('subscribed')
        const isTooManyRequests =
          Number(error?.statusCode) === 429 ||
          String(error?.message || '').includes('429') ||
          String(error?.message || '').toLowerCase().includes('too many requests')
        const errorMessage = isTooManyRequests
          ? error?.message || 'Too many requests'
          : 'Unable to send your water report. Please try again.'
        if (fromPageSubscription && !isTooManyRequests) this.modal?.show()
        if (fromPageSubscription) {
          subscribeForm?.showSubmitMessage(errorMessage, 'error')
        } else {
          this.showToast(errorMessage, 'error')
        }
      } finally {
        submitButton?.classList.remove('loading')
        if (submitButton) submitButton.disabled = false
        this.setBusy(false)
      }
    }

    async handleSendEmail() {
      if (this.busy) return

      const validationMessage = this.getEmailValidationMessage()
      if (validationMessage) {
        this.showEmailError(validationMessage)
        return
      }

      const email = this.querySelector('input[name="email"]')?.value.trim()
      this.clearEmailError()
      this.subscribedEmail = email

      const eventSource = this.emailButton?.getAttribute('data-wd-subscribe-event') || 'ewgpage'
      if (eventSource && typeof window.WDSendEvent === 'function') {
        window.WDSendEvent('wd_email_subscribe_click', { event_source: eventSource })
      }

      this.setBusy(true)
      if (this.emailButton) {
        this.emailButton.disabled = true
        this.emailButton.classList.add('loading')
      }
      try {
        const result = await window.WDEwgReportStore?.emailPdf(email)
        if (!result) throw new Error('Unable to send water report')
        if (Number(result.statusCode) === 429 || String(result.message || '').toLowerCase() === 'too many requests') {
          throw result
        }
        this.showToast('Your full water report has been sent to your email.', 'success')
      } catch (error) {
        const isTooManyRequests =
          Number(error?.statusCode) === 429 ||
          String(error?.message || '').includes('429') ||
          String(error?.message || '').toLowerCase().includes('too many requests')
        this.showToast(
          isTooManyRequests ? error?.message || 'Too many requests' : 'Unable to send your water report. Please try again.',
          'error'
        )
      } finally {
        if (this.emailButton) {
          this.emailButton.disabled = false
          this.emailButton.classList.remove('loading')
        }
        this.setBusy(false)
      }
    }

    showToast(message, type = 'success') {
      if (!this.toastEl || !message) return
      const textEl = this.toastEl.querySelector('.ewg-report-access-modal__toast-text')
      if (textEl) textEl.textContent = message
      this.toastEl.dataset.type = type
      this.toastEl.hidden = false
      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.toastEl.hidden = true
      }, 3000)
    }

    async handleViewReport() {
      if (!this.viewButton || this.busy) return

      if (typeof window.WDSendEvent === 'function') {
        window.WDSendEvent('wd_view_report_click')
      }

      this.setBusy(true)
      this.viewButton.disabled = true
      this.viewButton.classList.add('loading')
      try {
        const url = await window.WDEwgReportStore?.getPdfUrl()
        if (url) window.open(url, '_blank', 'noopener')
      } catch {
        this.showToast('Unable to open your water report. Please try again.', 'error')
      } finally {
        this.viewButton.disabled = false
        this.viewButton.classList.remove('loading')
        this.setBusy(false)
      }
    }

    handleAccessRequest(event) {
      this.setState(this.getInitialState())
      this.modal?.show(event.detail?.opener)
    }

    handleModalShow() {
      if (this.keepSuccessOnShow) {
        this.keepSuccessOnShow = false
      } else if (this.dataset.state === 'success') {
        this.setState('subscribed')
      } else {
        this.setState(this.getInitialState())
      }
      this.resetEmailInput()
    }

    resetEmailInput() {
      if (this.emailInput) this.emailInput.value = ''
      this.subscribedEmail = ''
      this.clearEmailError()
      if (this.toastEl) this.toastEl.hidden = true
    }

    setState(state) {
      // AI REVIEW / ACCEPTED RISK: 写入 localStorage 并广播解锁，仅同步前端展示。无需后端鉴权。
      this.dataset.state = state
      if (state !== 'subscribed' && state !== 'success') return

      window.localStorage.setItem(this.storageKey, 'subscribed')
      document.dispatchEvent(new CustomEvent('wd:ewg-report-unlocked', { detail: { state } }))
    }
  }

  customElements.define('ewg-report-query', EWGReportQuery)
  if (!customElements.get('ewg-report-access')) {
    customElements.define('ewg-report-access', EWGReportAccess)
  }
})()
